// Tab 1: Late Payment Fee policy.
//
// Provider-editable controls:
//   fee      — policyEnabled, gracePeriodDays, lateFeeAmount,
//              maxConsecutiveMonths, approvalMode
//   reminders — the three send days
//
// Reminder emails are part of the policy. Providers choose WHEN they send,
// never whether they send or what they say. The ladder copy is code-owned in
// defaults.ts.
//
// Deliberately NOT here: lateFeeType, maxLateFee, feeScope,
// graceAmountThreshold, subsidyInvoicesExempt. They stay in the stored
// shape and in defaults.ts — subsidyInvoicesExempt in particular is a
// compliance flag (states restrict late fees on subsidised slots) and
// must not be provider-editable in any form, disabled or read-only.
//
// approvalMode is surfaced as "automatically create invoices": off =>
// 'review_queue', on => 'auto'. The raw enum never reaches the UI.

import { Button, Input, Switch } from '@wonderschool/common-base-ui';
import { useTranslation } from 'react-i18next';
import type { LateFeePolicy } from '../types';
import ReminderEmailPreview from './ReminderEmailPreview';

// Mirrors the storage-layer backstop in api.ts. Out of range there is not
// clamped — it silently reverts to the platform default on the next read —
// so every numeric field the form writes is validated here too, and the
// save is blocked rather than letting a provider lose the value.
const MIN_GRACE_PERIOD_DAYS = 2; // 1 leaves no separate day for the "past due, no fee yet" email
const MAX_GRACE_PERIOD_DAYS = 30;
const MIN_LATE_FEE_AMOUNT = 0; // dollars, never cents
const MAX_PERCENT_FEE = 100;
const MAX_LATE_FEE_AMOUNT = 500;
const MIN_CONSECUTIVE_MONTHS = 1; // 0 reads as "policy on" while never charging anything
const MIN_REMINDER_DAYS_BEFORE = 1; // 0 duplicates the "due today" email
const MIN_FIRM_REMINDER_DAYS_AFTER = 1; // 0 would land the same day the invoice goes past due
const MIN_ESCALATION_DAYS_AFTER = 1;

// `Number('')` is 0 and `Number('-')` is NaN, so a bare min/max attribute on
// the input is not enough — check the value we would actually persist.
const outOfRange = (value: number | undefined, min: number, max = Number.MAX_SAFE_INTEGER, integerOnly = false) =>
  typeof value !== 'number' ||
  !Number.isFinite(value) ||
  value < min ||
  value > max ||
  (integerOnly && !Number.isInteger(value));

interface Props {
  policy: LateFeePolicy;
  isDirty: boolean;
  isSaving: boolean;
  canEdit: boolean;
  onChange: (patch: Partial<LateFeePolicy>) => void;
  onSave: () => void;
}

export default function LateFeePolicyTab({ policy, isDirty, isSaving, canEdit, onChange, onSave }: Props) {
  const { t } = useTranslation();

  const gracePeriodDays = policy.gracePeriodDays;
  const graceBad = outOfRange(gracePeriodDays, MIN_GRACE_PERIOD_DAYS, MAX_GRACE_PERIOD_DAYS, true);
  const isPercentFee = policy.lateFeeType === 'percent';
  const maxLateFeeAmount = isPercentFee ? MAX_PERCENT_FEE : MAX_LATE_FEE_AMOUNT;
  const feeAmountBad = outOfRange(policy.lateFeeAmount, MIN_LATE_FEE_AMOUNT, maxLateFeeAmount);
  const monthsBad = outOfRange(policy.maxConsecutiveMonths, MIN_CONSECUTIVE_MONTHS, Number.MAX_SAFE_INTEGER, true);
  const reminderBad = outOfRange(policy.reminderDaysBefore, MIN_REMINDER_DAYS_BEFORE, Number.MAX_SAFE_INTEGER, true);
  const firmBad = outOfRange(policy.firmReminderDaysAfter, MIN_FIRM_REMINDER_DAYS_AFTER, Number.MAX_SAFE_INTEGER, true);
  const escalationBad = outOfRange(
    policy.escalationDaysAfter,
    MIN_ESCALATION_DAYS_AFTER,
    Number.MAX_SAFE_INTEGER,
    true
  );
  const firmBeforeGrace = !graceBad && !firmBad && policy.firmReminderDaysAfter <= policy.gracePeriodDays;
  // Escalation is the last rung: sending it before the firm reminder inverts
  // the ladder. Only meaningful once both values are individually valid.
  const orderBad = !firmBad && !escalationBad && policy.escalationDaysAfter <= policy.firmReminderDaysAfter;
  const hasError =
    graceBad || feeAmountBad || monthsBad || reminderBad || firmBad || firmBeforeGrace || escalationBad || orderBad;

  const readOnly = !canEdit;

  return (
    <div data-testid="tab-panel-late-fee-policy" className="space-y-6">
      <section className="rounded-lg border bg-white p-5">
        {/* `id` is what gives the switch its accessible name: common-base-ui
            puts it on the headless-ui button and points the visible label's
            htmlFor at it. Without it both switches announce as unnamed. */}
        <Switch
          id="late-fee-policy-enabled"
          name="policyEnabled"
          checked={policy.policyEnabled}
          disabled={readOnly}
          onChange={(e) => onChange({ policyEnabled: e.target.checked })}
          label={
            <h2 className="whitespace-normal text-lg font-semibold text-gray-900">
              {t('billingSettings.lateFees.enableLabel')}
            </h2>
          }
          wrapperClasses="flex-row justify-between"
          data-testid="toggle-policy-enabled"
        />
        <p className="mt-1 text-sm text-gray-500">{t('billingSettings.lateFees.enableHelp')}</p>
      </section>

      <section className="rounded-lg border bg-white p-5">
        <h2 className="text-lg font-semibold text-gray-900">{t('billingSettings.lateFees.feeAndGraceHeading')}</h2>
        <p className="mt-1 text-sm text-gray-600">{t('billingSettings.lateFees.feeAndGraceDescription')}</p>

        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <Input
              name="gracePeriodDays"
              inputType="number"
              min={MIN_GRACE_PERIOD_DAYS}
              max={MAX_GRACE_PERIOD_DAYS}
              disabled={readOnly}
              label={t('billingSettings.lateFees.gracePeriodLabel')}
              value={gracePeriodDays}
              onChange={(e) => onChange({ gracePeriodDays: Number(e.target.value) })}
              error={graceBad ? t('billingSettings.lateFees.gracePeriodRange') : undefined}
              data-testid="input-grace-period-days"
            />
            <p className="mt-1 text-sm text-gray-500">{t('billingSettings.lateFees.gracePeriodHelp')}</p>
            {!graceBad && (
              <p className="mt-1 text-xs text-gray-500">
                {t('billingSettings.lateFees.graceHelp', { count: gracePeriodDays })}
              </p>
            )}
          </div>

          <div>
            <Input
              name="lateFeeAmount"
              inputType="number"
              min={MIN_LATE_FEE_AMOUNT}
              max={maxLateFeeAmount}
              disabled={readOnly}
              label={t(
                isPercentFee
                  ? 'billingSettings.lateFees.percentFeeAmountLabel'
                  : 'billingSettings.lateFees.feeAmountLabel'
              )}
              value={policy.lateFeeAmount}
              onChange={(e) => onChange({ lateFeeAmount: Number(e.target.value) })}
              error={
                feeAmountBad
                  ? t(
                      isPercentFee
                        ? policy.lateFeeAmount > MAX_PERCENT_FEE
                          ? 'billingSettings.lateFees.percentFeeAmountMaximum'
                          : 'billingSettings.lateFees.percentFeeAmountRange'
                        : policy.lateFeeAmount > MAX_LATE_FEE_AMOUNT
                          ? 'billingSettings.lateFees.feeAmountMaximum'
                          : 'billingSettings.lateFees.feeAmountRange'
                    )
                  : undefined
              }
              data-testid="input-late-fee-amount"
            />
            <p className="mt-1 text-sm text-gray-500">
              {t(
                isPercentFee
                  ? 'billingSettings.lateFees.percentFeeAmountHelp'
                  : 'billingSettings.lateFees.feeAmountHelp'
              )}
            </p>
          </div>

          <div>
            <Input
              name="maxConsecutiveMonths"
              inputType="number"
              min={MIN_CONSECUTIVE_MONTHS}
              disabled={readOnly}
              label={t('billingSettings.lateFees.maxConsecutiveMonthsLabel')}
              value={policy.maxConsecutiveMonths}
              onChange={(e) => onChange({ maxConsecutiveMonths: Number(e.target.value) })}
              error={monthsBad ? t('billingSettings.lateFees.maxConsecutiveMonthsRange') : undefined}
              data-testid="input-max-consecutive-months"
            />
            <p className="mt-1 text-sm text-gray-500">{t('billingSettings.lateFees.maxConsecutiveMonthsHelp')}</p>
          </div>
        </div>
      </section>

      <section className="rounded-lg border bg-white p-5">
        <h2 className="text-lg font-semibold text-gray-900">{t('billingSettings.reminders.heading')}</h2>
        <p className="mt-1 text-sm text-gray-600">{t('billingSettings.reminders.description')}</p>

        <div className="mt-5 grid gap-5 sm:grid-cols-3">
          <div>
            <Input
              name="reminderDaysBefore"
              inputType="number"
              min={MIN_REMINDER_DAYS_BEFORE}
              disabled={readOnly}
              label={t('billingSettings.reminders.beforeLabel')}
              value={policy.reminderDaysBefore}
              onChange={(e) => onChange({ reminderDaysBefore: Number(e.target.value) })}
              error={reminderBad ? t('billingSettings.reminders.beforeRange') : undefined}
              data-testid="input-reminder-days-before"
            />
            <p className="mt-1 text-sm text-gray-500">{t('billingSettings.reminders.beforeHelp')}</p>
          </div>

          <div>
            <Input
              name="firmReminderDaysAfter"
              inputType="number"
              min={MIN_FIRM_REMINDER_DAYS_AFTER}
              disabled={readOnly}
              label={t('billingSettings.reminders.firmLabel')}
              value={policy.firmReminderDaysAfter}
              onChange={(e) => onChange({ firmReminderDaysAfter: Number(e.target.value) })}
              error={
                firmBad
                  ? t('billingSettings.reminders.firmRange')
                  : firmBeforeGrace
                    ? t('billingSettings.reminders.firmAfterGrace')
                    : undefined
              }
              data-testid="input-firm-reminder-days-after"
            />
            <p className="mt-1 text-sm text-gray-500">{t('billingSettings.reminders.firmHelp')}</p>
          </div>

          <div>
            <Input
              name="escalationDaysAfter"
              inputType="number"
              min={MIN_ESCALATION_DAYS_AFTER}
              disabled={readOnly}
              label={t('billingSettings.reminders.escalationLabel')}
              value={policy.escalationDaysAfter}
              onChange={(e) => onChange({ escalationDaysAfter: Number(e.target.value) })}
              error={
                escalationBad
                  ? t('billingSettings.reminders.escalationRange')
                  : orderBad
                    ? t('billingSettings.reminders.escalationOrder')
                    : undefined
              }
              data-testid="input-escalation-days-after"
            />
            <p className="mt-1 text-sm text-gray-500">{t('billingSettings.reminders.escalationHelp')}</p>
          </div>
        </div>
      </section>

      <section className="rounded-lg border bg-white p-5">
        {/* The money path. Off => 'review_queue' (fees wait for approval),
            on => 'auto'. Inverting this auto-charges families. Locked by
            __tests__/LateFeePolicyTab.test.tsx. */}
        <Switch
          id="late-fee-approval-mode"
          name="approvalMode"
          checked={policy.approvalMode === 'auto'}
          disabled={readOnly}
          onChange={(e) => onChange({ approvalMode: e.target.checked ? 'auto' : 'review_queue' })}
          label={
            <h2 className="whitespace-normal text-lg font-semibold text-gray-900">
              {t('billingSettings.lateFees.autoInvoiceLabel')}
            </h2>
          }
          wrapperClasses="flex-row justify-between"
          data-testid="toggle-approval-mode"
        />
        <p className="mt-1 text-sm text-gray-500">{t('billingSettings.lateFees.autoInvoiceHelp')}</p>
      </section>

      <ReminderEmailPreview policy={policy} />

      <div className="sticky bottom-0 z-10 -mx-6 flex items-center justify-end gap-3 border-t border-[var(--border-decorative-light)] bg-white px-6 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 shadow-[0px_-2px_4px_0px_rgba(0,0,0,0.12)]">
        {isDirty && !isSaving && <span className="text-sm text-gray-500">{t('billingSettings.unsavedChanges')}</span>}
        <Button
          primary
          type="button"
          onClick={onSave}
          disabled={readOnly || !isDirty || isSaving || hasError}
          loading={isSaving}
          label={isSaving ? t('billingSettings.saving') : t('billingSettings.lateFees.saveButton')}
          data-testid="btn-save-policy"
        />
      </div>
    </div>
  );
}
